import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type PointerEvent } from 'react'
import { cellClipboardContent } from '../utils/copy-cells'

export type CellPosition = { row: number; column: number }
export type CellRange = { anchor: CellPosition; end: CellPosition }

export function useCellSelection(scope: string, rowCount: number, columnCount: number, values: (range: CellRange, footnotes: boolean) => string[][]) {
  const [selection, setSelection] = useState<{ scope: string; range: CellRange } | null>(null)
  const range = selection?.scope === scope ? selection.range : null
  const [textCell, setTextCell] = useState<CellPosition | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const drag = useRef<{ anchor: CellPosition; x: number; y: number; moved: boolean } | null>(null)
  const suppressClick = useRef(false)
  const touch = useRef<{ cell: CellPosition; x: number; y: number } | null>(null)
  const select = useCallback(
    (anchor: CellPosition, end: CellPosition) => {
      setSelection((current) =>
        current?.scope === scope &&
        current.range.anchor.row === anchor.row &&
        current.range.anchor.column === anchor.column &&
        current.range.end.row === end.row &&
        current.range.end.column === end.column
          ? current
          : { scope, range: { anchor, end } }
      )
    },
    [scope]
  )
  useEffect(() => {
    setSelection(null)
    setTextCell(null)
    drag.current = null
    touch.current = null
  }, [scope])

  const position = (target: EventTarget | null): CellPosition | null => {
    const cell = target instanceof Element ? target.closest<HTMLElement>('[data-copy-row]') : null
    if (!cell || !tableRef.current?.contains(cell)) return null
    return { row: Number(cell.dataset.copyRow), column: Number(cell.dataset.copyColumn) }
  }
  useEffect(() => {
    let frame = 0
    const move = (event: globalThis.PointerEvent) => {
      // Allow small finger movement during a tap without treating a swipe as selection.
      if (touch.current && Math.hypot(event.clientX - touch.current.x, event.clientY - touch.current.y) > 8) touch.current = null
      const current = drag.current
      if (!current) return
      current.x = event.clientX
      current.y = event.clientY
      const end = position(event.target)
      if (end && (current.moved || end.row !== current.anchor.row || end.column !== current.anchor.column)) {
        if (!current.moved) {
          tableRef.current?.focus({ preventScroll: true })
          setTextCell(null)
        }
        current.moved = true
        select(current.anchor, end)
        window.getSelection()?.removeAllRanges()
      }
      if (!frame && current.moved) frame = requestAnimationFrame(scroll)
    }
    const scroll = () => {
      const current = drag.current
      const viewport = tableRef.current?.parentElement
      if (current && viewport) {
        const bounds = viewport.getBoundingClientRect()
        const header = tableRef.current?.tHead?.getBoundingClientRect().bottom ?? bounds.top
        const dy = current.y < header + 24 ? -12 : current.y > bounds.bottom - 24 ? 12 : 0
        const dx = current.x < bounds.left + 24 ? -12 : current.x > bounds.right - 24 ? 12 : 0
        if ((dx || dy) && bounds.width > 0 && bounds.height > 0) {
          viewport.scrollTop += dy
          viewport.scrollLeft += dx
          const target = document.elementFromPoint(
            Math.max(bounds.left + 1, Math.min(current.x, bounds.right - 1)),
            Math.max(header + 1, Math.min(current.y, bounds.bottom - 1))
          )
          const end = position(target)
          if (end) {
            current.moved = true
            select(current.anchor, end)
          }
        }
      }
      frame = drag.current ? requestAnimationFrame(scroll) : 0
    }
    const stop = (event: globalThis.PointerEvent) => {
      const end = position(event.target)
      if (event.type === 'pointerup' && touch.current && end?.row === touch.current.cell.row && end.column === touch.current.cell.column) {
        setTextCell(null)
        select(end, end)
        tableRef.current?.focus({ preventScroll: true })
      }
      touch.current = null
      suppressClick.current = event.type === 'pointerup' && Boolean(end) && (suppressClick.current || Boolean(drag.current?.moved))
      drag.current = null
      cancelAnimationFrame(frame)
      frame = 0
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', stop)
    document.addEventListener('pointercancel', stop)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', stop)
      document.removeEventListener('pointercancel', stop)
    }
  }, [scope, select])

  const isTextCell = (row: number, column: number) => textCell?.row === row && textCell.column === column
  const isSelected = (row: number, column: number) =>
    Boolean(
      range &&
        row >= Math.min(range.anchor.row, range.end.row) &&
        row <= Math.max(range.anchor.row, range.end.row) &&
        column >= Math.min(range.anchor.column, range.end.column) &&
        column <= Math.max(range.anchor.column, range.end.column)
    )
  const pointerDown = (event: PointerEvent<HTMLTableElement>) => {
    suppressClick.current = false
    if (event.button !== 0) return
    const next = position(event.target)
    if (!next || !(event.target instanceof Element)) return
    if (event.target.closest('[role="textbox"], input, textarea') || isTextCell(next.row, next.column)) return
    const interactive = event.target.closest('a, button')
    if (event.pointerType === 'touch') {
      if (!interactive) touch.current = { cell: next, x: event.clientX, y: event.clientY }
      return
    }
    if (interactive && !event.shiftKey) {
      if (interactive.tagName === 'A') drag.current = { anchor: next, x: event.clientX, y: event.clientY, moved: false }
      return
    }
    event.preventDefault()
    setTextCell(null)
    const anchor = event.shiftKey && range ? range.anchor : next
    select(anchor, next)
    drag.current = { anchor, x: event.clientX, y: event.clientY, moved: false }
    suppressClick.current = event.shiftKey
    window.getSelection()?.removeAllRanges()
    tableRef.current?.focus({ preventScroll: true })
  }
  const keyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    if (event.key === 'Escape') {
      if (
        event.target !== tableRef.current &&
        (!(event.target instanceof Element) ||
          !position(event.target) ||
          event.target.closest('a, button, input, textarea, [role="textbox"], [role="menu"], [contenteditable]:not([contenteditable="false"])'))
      )
        return
      setTextCell(null)
      setSelection(null)
      return
    }
    if (event.target !== tableRef.current || !rowCount || !columnCount) return
    const end = range?.end ?? { row: 0, column: 0 }
    if (event.key === 'Enter') {
      event.preventDefault()
      setTextCell(end)
      const cell = tableRef.current?.querySelector<HTMLElement>(`[data-copy-row="${end.row}"][data-copy-column="${end.column}"]`)
      const content = cell?.querySelector<HTMLElement>('[data-table-copy-content]')
      content?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }))
      if (!cell?.querySelector('[role="textbox"]')) cell?.focus()
      return
    }
    const delta = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[event.key]
    if (!delta) return
    event.preventDefault()
    setTextCell(null)
    const next = range
      ? { row: Math.max(0, Math.min(rowCount - 1, end.row + delta[0])), column: Math.max(0, Math.min(columnCount - 1, end.column + delta[1])) }
      : end
    select(event.shiftKey && range ? range.anchor : next, next)
  }
  const copy = (event: ClipboardEvent<HTMLTableElement>) => {
    if (
      !range ||
      textCell ||
      (event.target instanceof Element && event.target.closest('input, textarea, [role="textbox"], [contenteditable]:not([contenteditable="false"])'))
    )
      return false
    const content = cellClipboardContent(values(range, false))
    event.clipboardData.setData('text/plain', content.text)
    event.clipboardData.setData('text/html', content.html)
    event.preventDefault()
    return true
  }
  return {
    range,
    textCell,
    tableRef,
    isSelected,
    isTextCell,
    copy,
    keyDown,
    pointerDown,
    clear: () => {
      setSelection(null)
      setTextCell(null)
    },
    doubleClick: (target: EventTarget) => {
      const cell = position(target)
      if (cell) setTextCell(cell)
    },
    suppressClick: () => {
      const suppress = suppressClick.current
      suppressClick.current = false
      return suppress
    },
  }
}
